import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import ServerSideSearch from '../ServerSideSearch';
import { useBrands } from '../../hooks/useBrands';
import { useSeller } from '../../hooks/useSeller';
import { API } from '../../config/config';
import { useAuth } from '../../hooks/useAuth';
import TextField from '@mui/material/TextField';

type ProductIterable = {
  id: string;
  title: string;
};

export interface IServerFilter {
  brandIds: string[];
  sellerIds: string[];
}

interface ServerFilterProps {
  onFilterChange?: (filterType: string, value: any) => void;
  onResultFetched?: (result: any) => void;
  products: ProductIterable[];
  hideProductName?: boolean;
  hideVendor?: boolean;
  hideBrand?: boolean;
  hideSearch?: boolean;
  onQueryChange?: (query: string) => void;
  setLoader?: React.Dispatch<React.SetStateAction<boolean>>;
  filtersAction?: React.Dispatch<React.SetStateAction<IServerFilter>>;
  endpoint?: string; // Optional endpoint for the search API
}

type selectedProduct = {
  id: string;
  title: string;
};

type selectedMenu = {
  id: string;
  name: string;
};

export const ServerFilter = (props: ServerFilterProps) => {
  const { enhancedFetch } = useAuth();
  // Get from the props
  const {
    onFilterChange,
    filtersAction,
    onResultFetched,
    setLoader,
    products,
    hideProductName,
    hideVendor,
    hideBrand,
    hideSearch,
    onQueryChange,
    endpoint
  } = props;
  const { brands } = useBrands();
  const { sellers } = useSeller();

  const [brandFilter, setBrandFilter] = useState<Array<selectedProduct>>([]);
  const [vendorFilter, setVendorFilter] = useState<Array<selectedProduct>>([]);
  const [productNameFilter, setProductNameFilter] = useState<
    Array<selectedProduct>
  >([]);
  const [productItems, setProductItems] = useState<ProductIterable[]>([]);
  const [cleared, setCleared] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(null);

  const searchHandler = async () => {
    setLoader && setLoader(true);
    // Use the provided endpoint or fall back to the default

    let _computedPayload = {
      brandIds: brandFilter.map((item) => item.id),
      sellerIds: vendorFilter.map((item) => item.id),
      query: searchTerm || ''
    } as any;

    // Remove empty arrays from the payload
    Object.keys(_computedPayload).forEach((key) => {
      if (
        Array.isArray(_computedPayload[key]) &&
        _computedPayload[key].length === 0
      ) {
        delete _computedPayload[key];
      }
    });

    const data: any = await enhancedFetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(_computedPayload)
    });
    if (data) {
      onResultFetched && onResultFetched(data);
      onQueryChange && onQueryChange(searchTerm);
      setLoader && setLoader(false);
    }
  };

  useEffect(() => {
    if (!cleared) return;
    setTimeout(() => {
      searchHandler().then(() => {
        setCleared(false);
      });
    }, 1000);
  }, [cleared]);

  useEffect(() => {
    filtersAction({
      brandIds: brandFilter.map((item) => item.id),
      sellerIds: vendorFilter.map((item) => item.id)
    });
  }, [brandFilter, vendorFilter]);

  useEffect(() => {
    setProductItems(products);
  }, [props.products]);

  return (
    <div className="server-filter">
      <Box sx={{ padding: 2, backgroundColor: '#f9f9f9' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Filter Products
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Brand Filter Dropdown: Multiselect */}
          {!hideSearch && (
            <>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <TextField
                  id="search-filter"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search by Product Name"
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
              </FormControl>
            </>
          )}

          {!hideBrand && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <>
                <InputLabel id="brand-filter-label">Brand</InputLabel>
                <Select
                  labelId="brand-filter-label"
                  multiple
                  id="brand-filter"
                  value={brandFilter as any}
                  label="Brand"
                  onChange={(e) => {
                    const value = e.target.value;
                    setBrandFilter(value);
                  }}
                  renderValue={(selected: selectedMenu[]) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((brandItem: selectedMenu) => (
                        <Chip
                          key={brandItem.id}
                          label={brandItem.name}
                          onDelete={() => {
                            setBrandFilter((prev) =>
                              prev.filter((item) => item.id !== brandItem.id)
                            );
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="">
                    <em>All Brands</em>
                  </MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand as any}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </>
            </FormControl>
          )}

          {/* Vendor Filter Dropdown: MultiSelect has been implemented for this:  */}

          {!hideVendor && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <>
                <InputLabel id="vendor-filter-label">Vendor</InputLabel>
                <Select
                  labelId="vendor-filter-label"
                  multiple
                  id="vendor-filter"
                  value={vendorFilter as any}
                  label="Vendor"
                  onChange={(e) => {
                    const value = e.target.value as any;
                    setVendorFilter(value);
                  }}
                  renderValue={(selected: selectedMenu[]) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((selected: selectedMenu) => (
                        <Chip
                          key={selected.id}
                          label={selected.name}
                          onDelete={() => {
                            setVendorFilter((prev) =>
                              prev.filter((item) => item.id !== selected.id)
                            );
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      onClick: (e) => {
                        e.stopPropagation();
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>All Vendors</em>
                  </MenuItem>
                  {sellers?.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor as any}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </>
            </FormControl>
          )}

          {/* Products Name Dropdown */}

          {!hideProductName && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <>
                <InputLabel id="product-name-filter-label">
                  Product Name
                </InputLabel>
                <Select
                  labelId="product-name-filter-label"
                  multiple
                  id="product-name-filter"
                  value={productNameFilter as any}
                  label="Product Name"
                  onChange={(e) => {
                    const value = e.target.value;
                    setProductNameFilter(value);
                  }}
                  renderValue={(selected: selectedProduct[]) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((selected: selectedProduct) => (
                        <Chip
                          key={selected.id}
                          label={selected.title}
                          onDelete={() => {
                            setProductNameFilter((prev) =>
                              prev.filter((item) => item.id !== selected.id)
                            );
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="">
                    <em>Products</em>
                  </MenuItem>
                  {productItems?.map((variant) => (
                    <MenuItem key={variant.id} value={variant as any}>
                      {variant.title}
                    </MenuItem>
                  ))}
                </Select>
              </>
            </FormControl>
          )}

          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            color="secondary"
            onClick={async () => await searchHandler()}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={async () => {
              setBrandFilter([]);
              setVendorFilter([]);
              setSearchTerm('');
              setCleared(true);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>
    </div>
  );
};
